import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { BodyEmailTemplate, UserNylasEmails } from '@numeo/types-core'
import { useAuth } from 'hooks/auth'
import { cn } from 'utils/cn'
import { showSuccess, showError } from 'utils/snackbar'
import { EmailConfig, getCurrentEmailConfig } from '../hooks/usePrivateLoadEmail'
import { OMIT_USERS_PARAMS } from '@/constants/auth-requests'

type CompanyDetail = NonNullable<UserNylasEmails['companyDetail']>

// Inject popover animation keyframes once
const POPOVER_STYLE_ID = 'load-hub-email-popover-styles'
const ensurePopoverStyles = () => {
    if (typeof document === 'undefined') return
    if (document.getElementById(POPOVER_STYLE_ID)) return
    const style = document.createElement('style')
    style.id = POPOVER_STYLE_ID
    style.textContent = `
        @keyframes loadHubPopoverIn {
            from { opacity: 0; transform: translateX(-4px) scale(0.98); }
            to   { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes loadHubPopoverInRight {
            from { opacity: 0; transform: translateX(4px) scale(0.98); }
            to   { opacity: 1; transform: translateX(0) scale(1); }
        }
    `
    document.head.appendChild(style)
}

/** Resolve templates: nylasEmail-level first, then user-level fallback */
const resolveTemplates = (nylasEmailTemplates: BodyEmailTemplate[] | undefined, userTemplates: BodyEmailTemplate[] | undefined): BodyEmailTemplate[] => {
    if (nylasEmailTemplates && nylasEmailTemplates.length > 0) return nylasEmailTemplates
    if (userTemplates && userTemplates.length > 0) return userTemplates
    return []
}

// ─────────────────────────────────────────────────────────────
// Company detail popover — shown on hover of an email row
// ─────────────────────────────────────────────────────────────

const DetailRow: React.FC<{ label: string; value?: string; tabular?: boolean }> = ({ label, value, tabular }) =>
    value ? (
        <>
            <span className="text-[9px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 self-start pt-[1px]">{label}</span>
            <span className={cn('text-[11px] font-medium text-gray-700 dark:text-gray-200 break-words', tabular && 'tabular-nums')}>{value}</span>
        </>
    ) : null

const CompanyDetailPopover: React.FC<{ email: string; detail: CompanyDetail | undefined; isPrimary: boolean }> = ({ email, detail, isPrimary }) => (
    <div
        className="relative w-full rounded-xl border border-gray-200/80 dark:border-[#2E2E2E] bg-white dark:bg-[#161616] p-3.5"
        style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, 0.06), 0 12px 32px -4px rgba(16, 24, 40, 0.14), 0 24px 48px -12px rgba(16, 24, 40, 0.18)',
        }}
    >
        <div className="flex items-start justify-between gap-2 mb-2">
            <span className="text-[11px] font-semibold text-gray-900 dark:text-gray-50 truncate leading-tight min-w-0">{email}</span>
            {isPrimary && (
                <span className="flex-shrink-0 text-[9px] font-semibold uppercase tracking-[0.08em] text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-500/10 border border-sky-200/60 dark:border-sky-500/20 px-1.5 py-[1px] rounded leading-none">
                    Primary
                </span>
            )}
        </div>
        {detail?.mcNumber && (
            <div className="flex items-center gap-1.5 mb-2.5">
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold tabular-nums text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/70 dark:border-emerald-500/20 px-1.5 py-[2px] rounded leading-none">
                    <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-emerald-600/80 dark:text-emerald-400/80">MC</span>
                    {detail.mcNumber}
                </span>
                {detail.dotNumber && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold tabular-nums text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200/70 dark:border-indigo-500/20 px-1.5 py-[2px] rounded leading-none">
                        <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-indigo-600/80 dark:text-indigo-400/80">DOT</span>
                        {detail.dotNumber}
                    </span>
                )}
            </div>
        )}
        {!detail ? (
            <div className="text-[11px] font-normal text-gray-400 dark:text-gray-500 italic">No company details configured</div>
        ) : (
            <>
                {detail.name && <div className="text-[13px] font-semibold text-gray-900 dark:text-gray-50 mb-2.5 truncate tracking-tight">{detail.name}</div>}
                <div className="grid grid-cols-[54px_1fr] gap-x-2 gap-y-1.5">
                    <DetailRow label="MC" value={detail.mcNumber} tabular />
                    <DetailRow label="DOT" value={detail.dotNumber} tabular />
                    <DetailRow label="Phone" value={detail.phoneNumber} tabular />
                    <DetailRow label="Transfer" value={detail.transferNumber} tabular />
                    <DetailRow label="Agent" value={detail.aiAgentName} />
                </div>
                {detail.companyAddress && (
                    <div className="mt-2.5 pt-2.5 border-t border-gray-100 dark:border-[#262626]">
                        <div className="text-[9px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500 mb-1">Address</div>
                        <div className="text-[11px] font-normal text-gray-600 dark:text-gray-300 leading-snug">{detail.companyAddress}</div>
                    </div>
                )}
                {detail.details && (
                    <div className="mt-2.5 pt-2.5 border-t border-gray-100 dark:border-[#262626]">
                        <div className="text-[9px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500 mb-1">Notes</div>
                        <div className="text-[11px] font-normal text-gray-600 dark:text-gray-300 leading-snug line-clamp-4">{detail.details}</div>
                    </div>
                )}
            </>
        )}
    </div>
)

// ─────────────────────────────────────────────────────────────
// Generic custom dropdown with optional hover popover per row
// ─────────────────────────────────────────────────────────────

type BadgeTone = 'sky' | 'emerald' | 'indigo' | 'gray'

interface BadgeItem {
    label: string
    value?: string
    tone: BadgeTone
}

interface DropdownItem {
    id: string
    value: string
    label: string
    sublabel?: string
    badges?: BadgeItem[]
    hoverContent?: React.ReactNode
    disabled?: boolean
}

const BADGE_TONE_CLASSES: Record<BadgeTone, string> = {
    sky: 'text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-500/10 border-sky-200/70 dark:border-sky-500/20',
    emerald: 'text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200/70 dark:border-emerald-500/20',
    indigo: 'text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200/70 dark:border-indigo-500/20',
    gray: 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-500/10 border-gray-200 dark:border-gray-500/20',
}

const BadgeChip: React.FC<{ badge: BadgeItem }> = ({ badge }) => (
    <span
        className={cn(
            'inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-[0.06em] border px-1.5 py-[2px] rounded leading-none flex-shrink-0',
            BADGE_TONE_CLASSES[badge.tone]
        )}
    >
        {badge.label}
        {badge.value && <span className="font-semibold tabular-nums normal-case tracking-normal">{badge.value}</span>}
    </span>
)

interface DropdownProps {
    value: string
    items: DropdownItem[]
    placeholder: string
    onChange: (value: string) => void
    onAddNew: () => void
    addNewLabel: string
    /** Optional leading icon; omit for a text-only trigger (Box Truck's sender picker). */
    leadingIcon?: React.ReactNode
    maxWidth?: number
    loading?: boolean
    /** Trigger height in px (default 36 — Load Hub). Box Truck uses a shorter trigger. */
    height?: number
    /** Solid white (surface) trigger background instead of the muted default fill. */
    solidBg?: boolean
}

const POPOVER_WIDTH = 300
/** Cap the open menu's width so long template subjects truncate instead of
 *  stretching the menu across (and under) neighboring UI like the sidebar. */
const MENU_MAX_WIDTH = 360

const Dropdown: React.FC<DropdownProps> = ({
    value,
    items,
    placeholder,
    onChange,
    onAddNew,
    addNewLabel,
    leadingIcon,
    maxWidth = 220,
    loading,
    height = 36,
    solidBg = false,
}) => {
    const [open, setOpen] = useState(false)
    // Menu opens to the RIGHT of the trigger (left-aligned, growing rightward) so it
    // never slides under the left sidebar; flips to right-aligned only when the
    // trigger sits too close to the right viewport edge. Computed on each open.
    const [menuAlign, setMenuAlign] = useState<'left' | 'right'>('left')
    const [hovered, setHovered] = useState<{ rect: DOMRect; content: React.ReactNode } | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const selected = items.find((i) => i.value === value)

    const toggleOpen = useCallback(() => {
        setOpen((prev) => {
            if (!prev && containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect()
                const menuWidth = Math.min(MENU_MAX_WIDTH, Math.max(maxWidth, 240))
                setMenuAlign(rect.left + menuWidth + 16 <= window.innerWidth ? 'left' : 'right')
            }
            return !prev
        })
    }, [maxWidth])

    useEffect(() => {
        ensurePopoverStyles()
    }, [])

    useEffect(() => {
        if (!open) {
            setHovered(null)
            return
        }
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
        }
        const keyHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        document.addEventListener('keydown', keyHandler)
        return () => {
            document.removeEventListener('mousedown', handler)
            document.removeEventListener('keydown', keyHandler)
        }
    }, [open])

    // Position popover on the opposite side if there's not enough space to the right
    const popoverPlacement = useMemo(() => {
        if (!hovered) return null
        const spaceRight = window.innerWidth - hovered.rect.right
        const side: 'right' | 'left' = spaceRight > POPOVER_WIDTH + 24 ? 'right' : 'left'
        const left = side === 'right' ? hovered.rect.right + 10 : hovered.rect.left - POPOVER_WIDTH - 10
        const top = Math.max(12, Math.min(window.innerHeight - 340, hovered.rect.top - 4))
        return { side, left, top, arrowTop: Math.max(12, hovered.rect.top + hovered.rect.height / 2 - top - 6) }
    }, [hovered])

    return (
        <div ref={containerRef} className="relative flex-shrink-0">
            <button
                type="button"
                onClick={toggleOpen}
                disabled={loading}
                className={cn(
                    'w-full flex items-center gap-1.5 pl-2.5 pr-2 rounded-md border transition-colors',
                    solidBg ? 'bg-[var(--lh-surface)]' : 'bg-[var(--lh-default,var(--lh-surface-secondary))]',
                    loading && 'opacity-60 cursor-wait',
                    !loading && 'cursor-pointer',
                    open
                        ? 'border-[var(--lh-accent)] shadow-[0_0_0_1px_var(--lh-accent)]'
                        : cn('border-[var(--lh-border)]', !solidBg && 'hover:bg-[var(--lh-surface-tertiary)]')
                )}
                style={{ maxWidth, height }}
            >
                {leadingIcon && <span className="flex-shrink-0 text-[var(--lh-fg-muted)] flex items-center">{leadingIcon}</span>}
                <span className="flex-1 text-left text-[12px] font-medium text-[var(--lh-fg)] truncate min-w-0">
                    {selected?.label || <span className="text-[var(--lh-fg-muted)] font-normal">{placeholder}</span>}
                </span>
                {loading ? (
                    <span className="w-3 h-3 border-2 border-[var(--lh-accent)] border-t-transparent rounded-full animate-spin flex-shrink-0" />
                ) : (
                    <svg
                        className={cn('w-3.5 h-3.5 text-[var(--lh-fg-muted)] flex-shrink-0 transition-transform duration-150', open && 'rotate-180')}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                )}
            </button>

            {open && (
                <div
                    className={cn(
                        'absolute top-[calc(100%+4px)] z-50 min-w-[240px] rounded-lg border border-gray-200/80 dark:border-[#2E2E2E] bg-white dark:bg-[#1A1A1A] shadow-xl shadow-black/[0.08] dark:shadow-black/40',
                        menuAlign === 'right' ? 'right-0' : 'left-0'
                    )}
                    style={{ minWidth: Math.max(maxWidth, 240), maxWidth: MENU_MAX_WIDTH }}
                >
                    <div className="py-1 max-h-[320px] overflow-y-auto rounded-t-lg">
                        {items.length === 0 ? (
                            <div className="px-3 py-3 text-center text-[11px] font-normal text-gray-400 dark:text-gray-500">No items</div>
                        ) : (
                            items.map((item) => {
                                const isSelected = item.value === value
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        disabled={item.disabled}
                                        onClick={() => {
                                            if (item.disabled) return
                                            onChange(item.value)
                                            setOpen(false)
                                        }}
                                        onMouseEnter={(e) => {
                                            if (item.hoverContent)
                                                setHovered({ rect: (e.currentTarget as HTMLElement).getBoundingClientRect(), content: item.hoverContent })
                                        }}
                                        onMouseLeave={() => setHovered(null)}
                                        className={cn(
                                            'w-full flex items-center gap-2 px-2.5 py-1.5 text-left bg-transparent border-none transition-colors duration-100',
                                            item.disabled && 'opacity-50 cursor-not-allowed',
                                            !item.disabled && 'cursor-pointer',
                                            isSelected
                                                ? 'bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400'
                                                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100/70 dark:hover:bg-[#252525]'
                                        )}
                                    >
                                        <div className="flex-1 flex flex-col min-w-0 leading-tight">
                                            <span className={cn('text-[11px] truncate', isSelected ? 'font-semibold' : 'font-medium')}>{item.label}</span>
                                            {item.badges && item.badges.length > 0 && (
                                                <div className="flex items-center gap-1 mt-1 flex-wrap">
                                                    {item.badges.map((b, i) => (
                                                        <BadgeChip key={`${b.label}-${i}`} badge={b} />
                                                    ))}
                                                </div>
                                            )}
                                            {item.sublabel && (
                                                <span className="text-[10px] font-normal text-gray-400 dark:text-gray-500 truncate mt-1">{item.sublabel}</span>
                                            )}
                                        </div>
                                        {isSelected && (
                                            <svg
                                                className="w-3.5 h-3.5 text-sky-500 flex-shrink-0"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="3"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        )}
                                    </button>
                                )
                            })
                        )}
                    </div>
                    <div className="border-t border-gray-100 dark:border-[#262626] rounded-b-lg">
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false)
                                onAddNew()
                            }}
                            className="w-full flex items-center gap-1.5 px-2.5 py-2 text-left text-[11px] font-semibold text-sky-500 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-500/10 transition-colors bg-transparent border-none cursor-pointer rounded-b-lg"
                        >
                            <svg
                                className="w-3.5 h-3.5 flex-shrink-0"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            {addNewLabel}
                        </button>
                    </div>
                </div>
            )}

            {open &&
                hovered &&
                popoverPlacement &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className="pointer-events-none"
                        style={{
                            position: 'fixed',
                            left: popoverPlacement.left,
                            top: popoverPlacement.top,
                            width: POPOVER_WIDTH,
                            zIndex: 2147483647,
                            animation: `${popoverPlacement.side === 'right' ? 'loadHubPopoverInRight' : 'loadHubPopoverIn'} 120ms cubic-bezier(0.16, 1, 0.3, 1) both`,
                            transformOrigin: popoverPlacement.side === 'right' ? 'left center' : 'right center',
                        }}
                    >
                        {/* Arrow pointer */}
                        <div
                            aria-hidden
                            className="absolute w-3 h-3 rotate-45 bg-white dark:bg-[#161616] border border-gray-200/80 dark:border-[#2E2E2E]"
                            style={{
                                top: popoverPlacement.arrowTop,
                                ...(popoverPlacement.side === 'right'
                                    ? { left: -6, borderRight: 'none', borderTop: 'none' }
                                    : { right: -6, borderLeft: 'none', borderBottom: 'none' }),
                            }}
                        />
                        {hovered.content}
                    </div>,
                    document.body
                )}
        </div>
    )
}

// ─────────────────────────────────────────────────────────────
// Icons
// ─────────────────────────────────────────────────────────────

const EmailIconSvg = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M3 7l9 6 9-6" />
    </svg>
)

const TemplateIconSvg = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <path d="M14 2v6h6" />
        <path d="M8 13h8M8 17h6" />
    </svg>
)

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────

interface PrivateLoadEmailSelectorProps {
    onConfigChange?: (config: EmailConfig) => void
    /** Hide the template dropdown, rendering only the "Send as" email picker. Box Truck's draft-email
     *  drawer drives the template via its own Info/Offer/Book tabs, so it only needs the sender picker. */
    showTemplateSelector?: boolean
    /** Box Truck styling for the email picker: no icon, shorter + wider, white bg. Load Hub untouched. */
    compactEmailPicker?: boolean
}

export const PrivateLoadEmailSelector: React.FC<PrivateLoadEmailSelectorProps> = ({
    onConfigChange,
    showTemplateSelector = true,
    compactEmailPicker = false,
}) => {
    const { user, setUser, setApplication } = useAuth()
    const navigate = useNavigate()
    const nylasEmails = useMemo(() => user?.nylasEmails || [], [user?.nylasEmails])

    const [selectedEmail, setSelectedEmail] = useState<string>('')
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('')
    const [isSwitching, setIsSwitching] = useState(false)

    // Track whether user has manually selected an email in this session.
    // Prevents the init effect from reverting the selection after Redux state refresh.
    const hasManuallySelectedRef = useRef(false)

    // Initialize from isPrimary — NOT localStorage (avoids cross-tab pollution).
    // After manual selection, only re-run if the selected email no longer exists (e.g. removed).
    useEffect(() => {
        if (nylasEmails.length === 0) return

        // If user manually selected an email that still exists, keep it
        if (hasManuallySelectedRef.current && nylasEmails.some((e) => e.email === selectedEmail)) return

        // Box Truck ONLY: honor an already-chosen sender from the in-memory config first (per-tab, not
        // localStorage → no cross-tab pollution) so the pick survives the compose's key-driven remount before
        // /auth/verify refreshes `isPrimary`. Gated on compactEmailPicker — Load Hub must NOT read the shared
        // singleton here, or a Box-Truck sender choice would leak into an unrelated Load Hub row's default.
        const configured = compactEmailPicker ? getCurrentEmailConfig()?.selectedEmail : undefined
        const initial = (configured && nylasEmails.find((e) => e.email === configured)) || nylasEmails.find((e) => e.isPrimary) || nylasEmails[0]
        if (!initial) return

        setSelectedEmail(initial.email)
        const tpls = resolveTemplates(initial.emailTemplates?.bodyTemplates, user?.emailTemplates?.bodyTemplates)
        const initialTemplateId = (tpls.find((t) => t.isPrimary) || tpls[0])?._id || ''
        setSelectedTemplateId(initialTemplateId)
        onConfigChange?.({ selectedEmail: initial.email, selectedTemplateId: initialTemplateId })
    }, [nylasEmails, user?.emailTemplates?.bodyTemplates, onConfigChange, selectedEmail, compactEmailPicker])

    const selectedNylasEmail = useMemo(() => nylasEmails.find((e) => e.email === selectedEmail), [nylasEmails, selectedEmail])

    const templates = useMemo<BodyEmailTemplate[]>(
        () => resolveTemplates(selectedNylasEmail?.emailTemplates?.bodyTemplates, user?.emailTemplates?.bodyTemplates),
        [selectedNylasEmail, user?.emailTemplates?.bodyTemplates]
    )

    const emailItems = useMemo<DropdownItem[]>(
        () =>
            nylasEmails.map((n) => ({
                id: n.email,
                value: n.email,
                label: n.email,
                sublabel: n.companyDetail?.name,
                badges: n.isPrimary ? [{ label: 'Primary', tone: 'sky' }] : undefined,
                hoverContent: <CompanyDetailPopover email={n.email} detail={n.companyDetail} isPrimary={n.isPrimary} />,
            })),
        [nylasEmails]
    )

    const templateItems = useMemo<DropdownItem[]>(
        () =>
            templates.map((t) => ({
                id: t._id || t.name || t.subject || Math.random().toString(),
                value: t._id || '',
                label: t.name || t.type || t.subject || 'Untitled',
                sublabel: t.subject && t.name !== t.subject ? t.subject : undefined,
                badges: t.isPrimary ? [{ label: 'Default', tone: 'sky' }] : undefined,
            })),
        [templates]
    )

    const handleEmailChange = useCallback(
        async (nextEmail: string) => {
            if (!user) return
            const nextNylas = nylasEmails.find((n) => n.email === nextEmail)
            if (!nextNylas) return
            if (nextEmail === selectedEmail) return

            const prevEmail = selectedEmail
            const prevTemplateId = selectedTemplateId

            // Mark as manually selected so init effect won't revert after Redux refresh
            hasManuallySelectedRef.current = true

            // Optimistic update — reflect in UI immediately
            setSelectedEmail(nextEmail)
            const tpls = resolveTemplates(nextNylas.emailTemplates?.bodyTemplates, user?.emailTemplates?.bodyTemplates)
            const nextTemplateId = (tpls.find((t) => t.isPrimary) || tpls[0])?._id || ''
            setSelectedTemplateId(nextTemplateId)
            onConfigChange?.({ selectedEmail: nextEmail, selectedTemplateId: nextTemplateId })

            try {
                setIsSwitching(true)
                await axios.post('/email/set-primary', { userId: user.id, email: nextEmail })

                // Refresh application/user so other components see the new primary
                try {
                    const res = await axios.get('/auth/verify', { params: OMIT_USERS_PARAMS })
                    if (res.data?.user) setUser(res.data.user)
                    if (res.data?.application) setApplication(res.data.application)
                } catch {
                    // Non-fatal — state refresh is best-effort
                }

                showSuccess('Primary email updated')
            } catch (err) {
                // Revert on failure
                hasManuallySelectedRef.current = false
                setSelectedEmail(prevEmail)
                setSelectedTemplateId(prevTemplateId)
                onConfigChange?.({ selectedEmail: prevEmail, selectedTemplateId: prevTemplateId })
                showError(err instanceof Error ? err.message : 'Failed to update primary email')
            } finally {
                setIsSwitching(false)
            }
        },
        [nylasEmails, user, selectedEmail, selectedTemplateId, setUser, setApplication, onConfigChange]
    )

    const handleTemplateChange = useCallback(
        (value: string) => {
            setSelectedTemplateId(value)
            onConfigChange?.({ selectedEmail, selectedTemplateId: value })
        },
        [selectedEmail, onConfigChange]
    )

    if (nylasEmails.length === 0) return null

    return (
        <div className="flex items-center gap-2 shrink-0">
            <Dropdown
                value={selectedEmail}
                items={emailItems}
                placeholder="Send as"
                onChange={handleEmailChange}
                onAddNew={() => navigate('/settings?tab=general')}
                addNewLabel="Add new email"
                leadingIcon={compactEmailPicker ? undefined : EmailIconSvg}
                maxWidth={compactEmailPicker ? 240 : 180}
                loading={isSwitching}
                height={compactEmailPicker ? 30 : 36}
                solidBg={compactEmailPicker}
            />
            {showTemplateSelector && (
                <Dropdown
                    value={selectedTemplateId}
                    items={templateItems}
                    placeholder="Email template"
                    onChange={handleTemplateChange}
                    onAddNew={() => navigate('/settings?tab=email-templates')}
                    addNewLabel="Add new template"
                    leadingIcon={TemplateIconSvg}
                    maxWidth={160}
                />
            )}
        </div>
    )
}
